import { useState, useEffect } from "react";

export type RepeatType = "none" | "daily" | "weekly" | "monthly";

export interface SendConfig {
  scheduleEnabled: boolean;
  scheduleDate: string;
  scheduleTime: string;
  repeatType: RepeatType;
  repeatEndDate: string;
}

interface Props {
  config: SendConfig;
  onChange: (config: SendConfig) => void;
  userId?: string;
}

const REPEAT_OPTIONS: { value: RepeatType; label: string; icon: string }[] = [
  { value: "none", label: "반복 없음", icon: "ri-close-circle-line" },
  { value: "daily", label: "매일", icon: "ri-calendar-line" },
  { value: "weekly", label: "매주", icon: "ri-calendar-2-line" },
  { value: "monthly", label: "매월", icon: "ri-calendar-event-line" },
];

function getToday(): string {
  return new Date().toISOString().split("T")[0];
}

function addMinutes(minutes: number): { date: string; time: string } {
  const d = new Date();
  d.setMinutes(d.getMinutes() + minutes);
  return {
    date: d.toISOString().split("T")[0],
    time: `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`,
  };
}

function getNextDay(hour: number, minute = 0): { date: string; time: string } {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(hour, minute, 0, 0);
  return {
    date: d.toISOString().split("T")[0],
    time: `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`,
  };
}

function getNextMonday(hour = 9): { date: string; time: string } {
  const d = new Date();
  const day = d.getDay();
  const daysUntilMonday = day === 0 ? 1 : 8 - day;
  d.setDate(d.getDate() + daysUntilMonday);
  d.setHours(hour, 0, 0, 0);
  return {
    date: d.toISOString().split("T")[0],
    time: `${String(d.getHours()).padStart(2, "0")}:00`,
  };
}

function formatScheduleLabel(date: string, time: string): string {
  if (!date || !time) return "";
  const d = new Date(`${date}T${time}`);
  const now = new Date();
  const diffMs = d.getTime() - now.getTime();
  const diffH = Math.floor(diffMs / (1000 * 60 * 60));
  const diffD = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  const weekdays = ["일", "월", "화", "수", "목", "금", "토"];
  const wd = weekdays[d.getDay()];
  const month = d.getMonth() + 1;
  const day = d.getDate();
  const hh = d.getHours();
  const mm = String(d.getMinutes()).padStart(2, "0");
  const ampm = hh < 12 ? "오전" : "오후";
  const h12 = hh % 12 === 0 ? 12 : hh % 12;

  const timeLabel = `${ampm} ${h12}:${mm}`;

  if (diffD === 0) return `오늘 ${timeLabel} (${diffH}시간 후)`;
  if (diffD === 1) return `내일 (${wd}) ${timeLabel}`;
  if (diffD < 7) return `${diffD}일 후 (${wd}) ${timeLabel}`;
  return `${month}월 ${day}일 (${wd}) ${timeLabel}`;
}

function getRepeatSummary(repeatType: RepeatType, endDate: string): string {
  if (repeatType === "none") return "";
  const labels: Record<RepeatType, string> = {
    none: "",
    daily: "매일",
    weekly: "매주",
    monthly: "매월",
  };
  const base = labels[repeatType];
  if (endDate) {
    const d = new Date(endDate);
    return `${base} · ${d.getMonth() + 1}월 ${d.getDate()}일까지`;
  }
  return `${base} 반복`;
}

export default function SendSettings({ config, onChange, userId }: Props) {
  const [timeError, setTimeError] = useState("");
  const [showRepeat, setShowRepeat] = useState(false);

  const update = (partial: Partial<SendConfig>) => onChange({ ...config, ...partial });

  // Validate schedule time
  useEffect(() => {
    if (!config.scheduleEnabled || !config.scheduleDate || !config.scheduleTime) {
      setTimeError("");
      return;
    }
    const selected = new Date(`${config.scheduleDate}T${config.scheduleTime}`);
    const now = new Date();
    const minTime = new Date(now.getTime() + 10 * 60 * 1000);
    if (selected < minTime) {
      setTimeError("현재 시각으로부터 최소 10분 이후로 설정해주세요");
    } else {
      setTimeError("");
    }
  }, [config.scheduleEnabled, config.scheduleDate, config.scheduleTime]);

  const PRESETS = [
    {
      label: "1시간 후",
      icon: "ri-time-line",
      action: () => {
        const { date, time } = addMinutes(60);
        update({ scheduleDate: date, scheduleTime: time });
      },
    },
    {
      label: "3시간 후",
      icon: "ri-time-line",
      action: () => {
        const { date, time } = addMinutes(180);
        update({ scheduleDate: date, scheduleTime: time });
      },
    },
    {
      label: "내일 오전 9시",
      icon: "ri-sun-line",
      action: () => {
        const { date, time } = getNextDay(9);
        update({ scheduleDate: date, scheduleTime: time });
      },
    },
    {
      label: "내일 오후 2시",
      icon: "ri-sun-foggy-line",
      action: () => {
        const { date, time } = getNextDay(14);
        update({ scheduleDate: date, scheduleTime: time });
      },
    },
    {
      label: "다음 주 월요일",
      icon: "ri-calendar-2-line",
      action: () => {
        const { date, time } = getNextMonday(9);
        update({ scheduleDate: date, scheduleTime: time });
      },
    },
  ];

  const scheduleLabel =
    config.scheduleDate && config.scheduleTime
      ? formatScheduleLabel(config.scheduleDate, config.scheduleTime)
      : "";

  const repeatSummary = getRepeatSummary(config.repeatType, config.repeatEndDate);

  return (
    <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-4 md:p-6">
      <div className="flex items-center gap-2 md:gap-3 mb-4 md:mb-5">
        <div className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center rounded-lg bg-gradient-to-br from-violet-500/30 to-pink-500/30">
          <i className="ri-settings-3-line text-violet-400 text-sm md:text-base"></i>
        </div>
        <div>
          <h3 className="text-white font-semibold text-sm md:text-base">발송 설정</h3>
          <p className="text-gray-500 text-[10px] md:text-xs mt-0.5">발송 시간 설정</p>
        </div>
      </div>

      {/* Schedule toggle */}
      <div className="mb-3 md:mb-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-white text-xs md:text-sm font-medium">예약 발송</p>
            <p className="text-gray-500 text-[10px] md:text-xs mt-0.5">원하는 시간에 자동 발송</p>
          </div>
          <button
            onClick={() => {
              const next = !config.scheduleEnabled;
              update({
                scheduleEnabled: next,
                scheduleDate: next ? "" : "",
                scheduleTime: next ? "" : "",
                repeatType: "none",
                repeatEndDate: "",
              });
              if (!next) setShowRepeat(false);
            }}
            className={`relative w-10 h-5 md:w-11 md:h-6 rounded-full transition-all cursor-pointer ${
              config.scheduleEnabled ? "bg-gradient-to-r from-violet-600 to-pink-600" : "bg-white/[0.1]"
            }`}
          >
            <span
              className={`absolute top-0.5 w-4 h-4 md:w-5 md:h-5 bg-white rounded-full shadow transition-all duration-200 ${
                config.scheduleEnabled ? "left-[20px] md:left-[22px]" : "left-0.5"
              }`}
            />
          </button>
        </div>
      </div>

      {/* Schedule panel */}
      {config.scheduleEnabled && (
        <div className="mt-2 space-y-3 md:space-y-4 p-3 md:p-4 bg-white/[0.03] rounded-xl border border-violet-500/20 animate-fadeIn">

          {/* Quick presets */}
          <div>
            <p className="text-gray-400 text-[10px] md:text-xs font-medium mb-1.5 md:mb-2">빠른 선택</p>
            <div className="flex flex-wrap gap-1.5 md:gap-2">
              {PRESETS.map((preset) => (
                <button
                  key={preset.label}
                  onClick={preset.action}
                  className="flex items-center gap-1 md:gap-1.5 px-2.5 md:px-3 py-1 md:py-1.5 bg-white/[0.05] hover:bg-violet-500/20 border border-white/[0.08] hover:border-violet-500/40 rounded-lg text-gray-300 hover:text-violet-300 text-[10px] md:text-xs font-medium cursor-pointer whitespace-nowrap transition-all"
                >
                  <i className={`${preset.icon} text-xs md:text-sm`}></i>
                  {preset.label}
                </button>
              ))}
            </div>
          </div>

          {/* Date & Time inputs */}
          <div>
            <p className="text-gray-400 text-[10px] md:text-xs font-medium mb-1.5 md:mb-2">직접 입력</p>
            <div className="grid grid-cols-2 gap-2 md:gap-3">
              <div>
                <label className="block text-gray-500 text-[10px] md:text-xs mb-1 md:mb-1.5">발송 날짜</label>
                <input
                  type="date"
                  value={config.scheduleDate}
                  min={getToday()}
                  onChange={(e) => update({ scheduleDate: e.target.value })}
                  className="w-full bg-white/[0.05] border border-white/[0.1] rounded-lg px-2.5 md:px-3 py-2 md:py-2.5 text-white text-xs md:text-sm focus:outline-none focus:border-violet-500/60 transition-all cursor-pointer"
                />
              </div>
              <div>
                <label className="block text-gray-500 text-[10px] md:text-xs mb-1 md:mb-1.5">발송 시간</label>
                <input
                  type="time"
                  value={config.scheduleTime}
                  onChange={(e) => update({ scheduleTime: e.target.value })}
                  className="w-full bg-white/[0.05] border border-white/[0.1] rounded-lg px-2.5 md:px-3 py-2 md:py-2.5 text-white text-xs md:text-sm focus:outline-none focus:border-violet-500/60 transition-all cursor-pointer"
                />
              </div>
            </div>

            {/* Time error */}
            {timeError && (
              <div className="mt-1.5 md:mt-2 flex items-center gap-1.5 md:gap-2 text-red-400">
                <div className="w-3 h-3 md:w-4 md:h-4 flex items-center justify-center">
                  <i className="ri-error-warning-line text-xs md:text-sm"></i>
                </div>
                <p className="text-[10px] md:text-xs">{timeError}</p>
              </div>
            )}
          </div>

          {/* Schedule summary card */}
          {scheduleLabel && !timeError && (
            <div className="flex items-center gap-2 md:gap-3 px-3 md:px-4 py-2.5 md:py-3 bg-gradient-to-r from-violet-600/15 to-pink-600/10 rounded-xl border border-violet-500/25">
              <div className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center rounded-lg bg-violet-500/20 shrink-0">
                <i className="ri-calendar-check-line text-violet-400 text-sm md:text-base"></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-violet-300 text-[10px] md:text-xs font-semibold">예약 발송 예정</p>
                <p className="text-white text-xs md:text-sm font-bold mt-0.5 truncate">{scheduleLabel}</p>
                {repeatSummary && (
                  <p className="text-pink-400 text-[10px] md:text-xs mt-0.5">{repeatSummary}</p>
                )}
              </div>
              <button
                onClick={() => update({ scheduleDate: "", scheduleTime: "" })}
                className="w-5 h-5 md:w-6 md:h-6 flex items-center justify-center text-gray-500 hover:text-gray-300 cursor-pointer transition-colors shrink-0"
              >
                <i className="ri-close-line text-xs md:text-sm"></i>
              </button>
            </div>
          )}

          {/* Repeat section */}
          <div>
            <button
              onClick={() => setShowRepeat(!showRepeat)}
              className="flex items-center gap-1.5 md:gap-2 text-gray-400 hover:text-gray-200 text-[10px] md:text-xs font-medium cursor-pointer transition-colors"
            >
              <i className={`ri-repeat-line text-xs md:text-sm transition-transform ${showRepeat ? "text-violet-400" : ""}`}></i>
              반복 발송 설정
              <i className={`ri-arrow-down-s-line text-xs md:text-sm transition-transform ${showRepeat ? "rotate-180 text-violet-400" : ""}`}></i>
            </button>

            {showRepeat && (
              <div className="mt-2.5 md:mt-3 space-y-2.5 md:space-y-3">
                {/* Repeat type */}
                <div className="grid grid-cols-4 gap-1 md:gap-1.5">
                  {REPEAT_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => update({ repeatType: opt.value })}
                      className={`flex flex-col items-center gap-0.5 md:gap-1 py-2 md:py-2.5 rounded-lg border cursor-pointer whitespace-nowrap transition-all text-[10px] md:text-xs font-medium ${
                        config.repeatType === opt.value
                          ? "bg-violet-500/20 border-violet-500/40 text-violet-300"
                          : "bg-white/[0.03] border-white/[0.08] text-gray-500 hover:text-gray-300 hover:border-white/[0.15]"
                      }`}
                    >
                      <i className={`${opt.icon} text-sm md:text-base`}></i>
                      {opt.label}
                    </button>
                  ))}
                </div>

                {/* Repeat end date */}
                {config.repeatType !== "none" && (
                  <div>
                    <label className="block text-gray-500 text-[10px] md:text-xs mb-1 md:mb-1.5">반복 종료일 (선택)</label>
                    <input
                      type="date"
                      value={config.repeatEndDate}
                      min={config.scheduleDate || getToday()}
                      onChange={(e) => update({ repeatEndDate: e.target.value })}
                      className="w-full bg-white/[0.05] border border-white/[0.1] rounded-lg px-2.5 md:px-3 py-2 md:py-2.5 text-white text-xs md:text-sm focus:outline-none focus:border-violet-500/60 transition-all cursor-pointer"
                    />
                    <p className="text-gray-600 text-[10px] md:text-xs mt-1">설정하지 않으면 수동으로 취소할 때까지 반복됩니다</p>
                  </div>
                )}

                {config.repeatType !== "none" && (
                  <div className="flex items-start gap-1.5 md:gap-2 px-2.5 md:px-3 py-2 md:py-2.5 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                    <div className="w-3 h-3 md:w-4 md:h-4 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="ri-information-line text-amber-400 text-xs md:text-sm"></i>
                    </div>
                    <p className="text-amber-300/80 text-[10px] md:text-xs leading-relaxed">
                      반복 발송은 예약 발송 페이지에서 개별 취소할 수 있습니다.
                      각 발송마다 크레딧이 차감됩니다.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}